import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabaseClient } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({
  session: null,
  user: null,
  loading: true,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    loading: true,
  });
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    // Initial session check
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabaseClient.auth.getSession();
        
        if (error) throw error;
        
        if (mounted) {
          setState(current => ({ 
            ...current, 
            session, 
            user: session?.user ?? null,
            loading: false 
          }));

          // If no session and not on login page, redirect to login
          if (!session && location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
        if (mounted) {
          setState(current => ({
            ...current,
            session: null,
            user: null,
            loading: false
          }));
          navigate('/login', { replace: true });
        }
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        try {
          if (event === 'SIGNED_OUT') {
            setState(current => ({
              ...current,
              session: null,
              user: null,
              loading: false
            }));
            navigate('/login', { replace: true });
          } else if (session) {
            setState(current => ({ 
              ...current, 
              session, 
              user: session.user,
              loading: false 
            }));
          } else {
            setState(current => ({
              ...current,
              session: null,
              user: null,
              loading: false
            }));
            navigate('/login', { replace: true });
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          if (mounted) {
            setState(current => ({
              ...current,
              session: null,
              user: null,
              loading: false
            }));
            navigate('/login', { replace: true });
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}